/**
 * useKitchenPacketPrint — owns the Print Orders packet's print lifecycle.
 *
 * iPhone and iPad print through UIKit, which keeps the page count of its first
 * pagination pass and draws only that many pages: a long page print silently
 * lost its tail (the last school of a chef's packet). A PDF in a frame does
 * not help there — iOS draws a framed PDF as an image of its first page. So on
 * those devices the packet (the real print components, measured contents
 * included) becomes a FINISHED PDF that is handed to the share sheet, whose
 * Print prints the file's own pages and whose Save to Files keeps it.
 *
 *   pdf mode:  (iPhone, iPad) dialog open → selection measured → markup
 *              captured → PDF prepared in the background (debounced) →
 *              "ready, N pages" → Print shares the PDF inside the tap.
 *   page mode: (desktop, Android) Print mounts the same packet with
 *              `flushSync` and calls `window.print()` in the same tick. These
 *              browsers paginate once, so the page prints whole.
 *
 * Nothing is torn down on `afterprint` or a timer — iOS fires `afterprint`
 * after each render pass while its print sheet is still open. The prepared
 * PDF / mounted packet lives until the next prepare, a kitchen or range
 * change, or leaving the page.
 */

import { toast } from '@ordo/ui/sonner'
import { type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

import { ApiError } from '@/api/http'
import { fetchKitchenPacketPdf, type KitchenPacketPdf, type KitchenPacketPdfRequest } from '@/api/kitchen-orders'
import { PacketMarkupCapture, RetainedPrintPortal } from '@/components/chefs/print/kitchen-packet-print-hosts'
import type { PacketPlan } from '@/components/chefs/print/packet/packet-measure-host'
import type { PacketLayout } from '@/lib/chefs/packet/layout'
import { packetModelFor } from '@/lib/chefs/packet/model'
import { packetDocumentTitle, type PacketTarget } from '@/lib/chefs/packet/target'
import { clientStylesheetHref, isAppleTouchDevice } from '@/lib/print/pdf-print-capability'
import { isShareDismissal, pdfFileName, PdfShareSheet } from '@/lib/print/pdf-share-sheet'

export type PacketPrintMode = 'pdf' | 'page'

export type PacketPrintStatus =
  | { state: 'measuring' }
  | { state: 'preparing' }
  | { state: 'ready'; pages: number | null }
  | { state: 'error'; message: string; action: 'retry' | 'reload' }

/** The slice of `PdfShareSheet` the hook drives — a seam for tests. */
export interface PacketPdfHandoff {
  deliver(pdf: Blob, fileName: string): Promise<void>
  dispose(): void
}

export interface UseKitchenPacketPrintInput {
  kitchenLocationId: string | null
  dialogOpen: boolean
  /** The dialog's current selection. */
  selection: PacketTarget | null
  /** The latest measurement; used only when it belongs to `selection`. */
  measured: PacketPlan | null
  /** Kitchen + visible range. A change drops anything prepared or mounted. */
  scopeKey: string
  /** Test seams. */
  mode?: PacketPrintMode
  fetchPdf?: (req: KitchenPacketPdfRequest, signal: AbortSignal) => Promise<KitchenPacketPdf>
  createHandoff?: () => PacketPdfHandoff
  printPage?: () => void
  notifyError?: (message: string) => void
  stylesheetHref?: () => string | null
  debounceMs?: number
}

export interface KitchenPacketPrint {
  mode: PacketPrintMode
  status: PacketPrintStatus
  /** True when Print would print the current selection right now. */
  canPrint: boolean
  /** Print `target` synchronously (call it inside the tap). False = not ready; keep the dialog open. */
  print: (target: PacketTarget) => boolean
  /** Prepare the current selection again after an error. */
  retry: () => void
  /** A print this hook owns is live — suppresses usePrintAction's "unhandled system print" leg. */
  printing: boolean
  /** Portals to render once, anywhere in the page. */
  hosts: ReactNode
}

/** Settle time before preparing a PDF, so checkbox flips don't each start a render. */
export const PACKET_PREPARE_DEBOUNCE_MS = 400

/** Shown when the share sheet could not open (never for the user dismissing it). */
export const SHARE_FAILED_MESSAGE = "Couldn't open the print sheet. Try again."

interface Capture {
  version: number
  scopeKey: string
  signature: string
  html: string
}

/**
 * Error codes whose server message is written for the chef, shown as-is. Any
 * other failure (a dropped connection, an unexpected status) gets the generic
 * retry line, which would misdirect these: a server with no PDF service is
 * not a connection problem, and a too-large packet needs fewer days.
 */
const SERVER_WORDED_ERRORS: ReadonlySet<string> = new Set([
  'temp_log_gate',
  'unavailable',
  'payload_too_large',
  'too_complex',
  'pdf_render_timeout',
  'pdf_render_failed',
])

/** User-facing words for a failed prepare. An outdated tab must reload; anything else can retry. */
export function describePacketPrintError(error: unknown): { message: string; action: 'retry' | 'reload' } {
  if (error instanceof ApiError) {
    if (error.code === 'client_outdated') {
      return { message: 'Ordo was updated. Reload the page to print.', action: 'reload' }
    }
    if (error.status === 429) {
      return { message: 'Too many prints in a row. Try again in a few minutes.', action: 'retry' }
    }
    if (error.code !== null && SERVER_WORDED_ERRORS.has(error.code)) {
      return { message: error.message, action: 'retry' }
    }
  }
  return { message: "Couldn't prepare the print. Check your connection and try again.", action: 'retry' }
}

export function useKitchenPacketPrint(input: UseKitchenPacketPrintInput): KitchenPacketPrint {
  const { dialogOpen, selection, measured, scopeKey } = input
  const [mode] = useState<PacketPrintMode>(() => input.mode ?? (isAppleTouchDevice() ? 'pdf' : 'page'))

  const selectionSignature = selection ? packetModelFor(selection).signature : null
  // The measurement of the CURRENT selection — a layout measured for another
  // selection is never used (its pages hold other words).
  const measuredLayout: PacketLayout | null =
    selection && measured && measured.signature === selectionSignature ? measured.layout : null

  // Everything the async prepare reads, kept current without re-running it.
  const latest = useRef(input)
  latest.current = input

  // ---- pdf mode --------------------------------------------------------

  const handoffRef = useRef<PacketPdfHandoff | null>(null)
  const handoff = useCallback((): PacketPdfHandoff => {
    handoffRef.current ??= latest.current.createHandoff?.() ?? new PdfShareSheet()
    return handoffRef.current
  }, [])

  const [capture, setCapture] = useState<Capture | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [prepared, setPrepared] = useState<{ key: string; pages: number | null; pdf: Blob } | null>(null)
  const [failure, setFailure] = useState<{ key: string; message: string; action: 'retry' | 'reload' } | null>(null)

  const onMarkup = useCallback(
    (html: string) => {
      if (!selectionSignature) {
        return
      }
      setCapture((prev) =>
        prev && prev.html === html && prev.signature === selectionSignature && prev.scopeKey === scopeKey
          ? prev
          : { version: (prev?.version ?? 0) + 1, scopeKey, signature: selectionSignature, html },
      )
    },
    [selectionSignature, scopeKey],
  )

  // Identity of the PDF the current selection needs; null while it isn't captured yet.
  const currentKey =
    mode === 'pdf' && capture && capture.scopeKey === scopeKey && capture.signature === selectionSignature
      ? `${scopeKey}#${capture.version}.${attempt}`
      : null
  const captureRef = useRef(capture)
  captureRef.current = capture
  const preparedKey = prepared?.key ?? null
  const failedKey = failure?.key ?? null

  useEffect(() => {
    // Already prepared, or already failed (Retry bumps `attempt` → new key).
    if (mode !== 'pdf' || !dialogOpen || !currentKey || currentKey === preparedKey || currentKey === failedKey) {
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      const { kitchenLocationId: kitchenId, selection: target, measured: plan } = latest.current
      const markup = captureRef.current
      if (!kitchenId || !target || !markup) {
        return
      }
      // Audit metadata only: the days the packet actually covers.
      const startDate = target.days[0]?.iso
      const endDate = target.days[target.days.length - 1]?.iso
      const fetchPdf = latest.current.fetchPdf ?? fetchKitchenPacketPdf
      const stylesheetHref = latest.current.stylesheetHref ?? clientStylesheetHref
      void fetchPdf(
        {
          kitchenLocationId: kitchenId,
          title: packetDocumentTitle(target),
          markup: markup.html,
          cssHref: stylesheetHref(),
          startDate,
          endDate,
          expectedPages: plan?.totalPages,
        },
        controller.signal,
      )
        .then((pdf) => {
          if (!controller.signal.aborted) {
            setPrepared({ key: currentKey, pages: pdf.pages, pdf: pdf.blob })
          }
        })
        .catch((error: unknown) => {
          if (!controller.signal.aborted) {
            setFailure({ key: currentKey, ...describePacketPrintError(error) })
          }
        })
    }, latest.current.debounceMs ?? PACKET_PREPARE_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [mode, dialogOpen, currentKey, preparedKey, failedKey])

  // A kitchen / range change (and leaving the page) releases the share sheet's PDF.
  useEffect(() => {
    return () => {
      handoffRef.current?.dispose()
    }
  }, [scopeKey])

  // ---- page mode -------------------------------------------------------

  const [retained, setRetained] = useState<{
    scopeKey: string
    target: PacketTarget
    layout: PacketLayout
    title: string
  } | null>(null)
  const retainedForScope = retained && retained.scopeKey === scopeKey ? retained : null

  // Reopening the dialog starts a new print: drop the last one's packet so a
  // browser-menu print can't reprint an old selection.
  useEffect(() => {
    if (mode === 'page' && dialogOpen) {
      setRetained(null)
    }
  }, [mode, dialogOpen])

  // The page is named after the packet while it is mounted (the print job and
  // Save-as-PDF names); restored when it goes. A LAYOUT effect so it runs
  // inside `print`'s `flushSync` — before `window.print()` reads the title.
  useLayoutEffect(() => {
    if (!retainedForScope) {
      return
    }
    const previousTitle = document.title
    document.title = retainedForScope.title
    return () => {
      document.title = previousTitle
    }
  }, [retainedForScope])

  // ---- public surface ----------------------------------------------------

  const status: PacketPrintStatus = useMemo(() => {
    if (mode === 'page') {
      return measuredLayout ? { state: 'ready', pages: measured?.totalPages ?? null } : { state: 'measuring' }
    }
    if (failure && failure.key === currentKey) {
      return { state: 'error', message: failure.message, action: failure.action }
    }
    if (prepared && prepared.key === currentKey) {
      return { state: 'ready', pages: prepared.pages }
    }
    return measuredLayout ? { state: 'preparing' } : { state: 'measuring' }
  }, [mode, measuredLayout, measured, failure, prepared, currentKey])

  const canPrint = status.state === 'ready'

  const print = useCallback(
    (target: PacketTarget): boolean => {
      if (!canPrint || packetModelFor(target).signature !== selectionSignature) {
        return false
      }
      const title = packetDocumentTitle(target)
      if (mode === 'pdf') {
        if (!prepared || prepared.key !== currentKey) {
          return false
        }
        const notify = latest.current.notifyError ?? ((message: string) => toast.error(message))
        let sharing: Promise<void>
        try {
          sharing = handoff().deliver(prepared.pdf, pdfFileName(title))
        } catch {
          notify(SHARE_FAILED_MESSAGE)
          return false
        }
        sharing.catch((error: unknown) => {
          if (!isShareDismissal(error)) {
            notify(SHARE_FAILED_MESSAGE)
          }
        })
        return true
      }
      if (!measuredLayout) {
        return false
      }
      flushSync(() => {
        setRetained({ scopeKey, target, layout: measuredLayout, title })
      })
      ;(latest.current.printPage ?? (() => window.print()))()
      return true
    },
    [canPrint, selectionSignature, mode, prepared, currentKey, handoff, measuredLayout, scopeKey],
  )

  const retry = useCallback(() => {
    if (failure?.action === 'reload') {
      window.location.reload()
      return
    }
    setAttempt((n) => n + 1)
  }, [failure])

  // The share sheet prints the file, never this page, so only page mode owns a page print.
  const printing = mode === 'page' && retainedForScope !== null

  const hosts = (
    <>
      {mode === 'pdf' && dialogOpen && selection && measuredLayout ? (
        <PacketMarkupCapture target={selection} layout={measuredLayout} onMarkup={onMarkup} />
      ) : null}
      {retainedForScope ? (
        <RetainedPrintPortal target={retainedForScope.target} layout={retainedForScope.layout} />
      ) : null}
    </>
  )

  return { mode, status, canPrint, print, retry, printing, hosts }
}
